import { type RefObject, useRef } from 'react';
import pick from 'lodash-es/pick';

import {
  ShortlistContactButtonClickEvent,
  ShortlistOpenEvent,
} from '@rover/components/src/shortlist/analyticsEvents';
import useShortlist from '@rover/components/src/shortlist/hooks/useShortlist';
import { Props as ButtonProps } from '@rover/kibble/core/Button';
import useRefocusIfOpenerGone from '@rover/react-lib/src/hooks/useRefocusIfOpenerGone';
import type { EnhancedPersonSitterProfile } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfile';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import ShortlistAddButton from './ShortlistAddButton';
import ShortlistContinueButtons from './ShortlistContinueButtons';
import type { PagePosition, ShortlistLayout } from './types';

const MARGIN_PROPS = ['m', 'mt', 'mr', 'mb', 'ml', 'mx', 'my'] as const;

type Props = ButtonProps & {
  sitter: EnhancedPersonSitterProfile | undefined;
  pagePosition: PagePosition;
  layout: ShortlistLayout;
  primaryButtonRef?: RefObject<HTMLElement | null>;
};

const ShortlistContactActions = ({
  sitter,
  pagePosition,
  layout,
  primaryButtonRef,
  ...other
}: Props): JSX.Element => {
  const { isAdded, count, isModalVisible, setIsModalVisible } = useShortlist();
  const localPrimaryRef = useRef<HTMLElement>(null);
  const primaryRef = primaryButtonRef ?? localPrimaryRef;
  const viewListRef = useRef<HTMLElement>(null);
  // Adding or removing this sitter replaces the button that opened the list.
  const rememberListOpener = useRefocusIfOpenerGone({
    isOpen: isModalVisible,
    getTarget: () => primaryRef.current,
  });

  const openList = (opener: HTMLElement | null, listCount: number): void => {
    emitAnalyticsEvent(new ShortlistOpenEvent({ source: 'sitter-profile', count: listCount }));
    rememberListOpener(opener);
    setIsModalVisible(true);
  };

  const handleContinue = (): void => {
    new ShortlistContactButtonClickEvent({ source: 'sitter-profile', count }).fireCrossPageEvent();
  };

  if (isAdded(sitter?.opk ?? '')) {
    // These act on the whole list, so they ignore `disabled`, which reflects this sitter's service.
    return (
      <ShortlistContinueButtons
        layout={layout}
        pagePosition={pagePosition}
        margins={pick(other, MARGIN_PROPS)}
        continueRef={primaryRef}
        viewListRef={viewListRef}
        onContinue={handleContinue}
        onViewList={() => openList(viewListRef.current, count)}
      />
    );
  }

  return (
    <ShortlistAddButton
      sitter={sitter}
      layout={layout}
      pagePosition={pagePosition}
      buttonRef={primaryRef}
      // Surface the list (the modal renders over the profile) instead of dead-ending.
      onAdded={(updatedCount) => openList(primaryRef.current, updatedCount)}
      {...other}
    />
  );
};

export default ShortlistContactActions;
